if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        this.offset = 0;
        this.updateOffset = this.updateOffset.bind(this);
        this.onScroll = this.onScroll.bind(this);

        this.updateOffset();
        window.addEventListener('resize', this.updateOffset);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        this.setupMobileMenu();
        this.setupCardScrollers();
        this.setupMegamenuClose();

        // Web fonts can change the link height after first paint.
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.updateDropdownGap());
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.updateOffset);
        window.removeEventListener('scroll', this.onScroll);
      }

      updateOffset() {
        const marquee = document.querySelector('.marquee-bar');
        this.offset = marquee ? marquee.offsetHeight : 0;
        this.style.setProperty('--site-header-offset', `${this.offset}px`);
        this.updateDropdownGap();
        this.updateHeaderBottom();
      }

      // Distance from the bottom of a nav link to the bottom of the bar, so the
      // small dropdowns open flush against the bar with no gap.
      updateDropdownGap() {
        const bar = this.querySelector('.site-header__bar');
        const item = this.querySelector('.site-header__nav-item');
        if (!bar || !item) return;
        const gap = bar.getBoundingClientRect().bottom - item.getBoundingClientRect().bottom;
        if (gap >= 0) this.style.setProperty('--site-header-dropdown-gap', `${gap}px`);
      }

      // Bottom of the header bar, for the phone search panel and cart, which
      // rise up to it and stop under it (site-search.css, site-cart-drawer.css).
      updateHeaderBottom() {
        const bar = this.querySelector('.site-header__bar');
        if (!bar) return;
        const bottom = Math.max(Math.round(bar.getBoundingClientRect().bottom), 0);
        if (bottom !== this.headerBottom) {
          this.headerBottom = bottom;
          document.documentElement.style.setProperty('--ys-header-bottom', `${bottom}px`);
        }
      }

      onScroll() {
        this.classList.toggle('is-scrolled', window.scrollY > Math.max(this.offset - 4, 0));
        this.updateHeaderBottom();
        // iPhones still scroll the page behind the open menu: keep the panel
        // stuck to the header bar so no strip of the page shows between them.
        if (this.classList.contains('mobile-menu-open') && this.setPanelTop) this.setPanelTop();
      }

      setupMobileMenu() {
        const toggle = this.querySelector('[data-mobile-toggle]');
        const panel = this.querySelector('[data-mobile-panel]');
        if (!toggle || !panel) return;

        const openLabel = toggle.getAttribute('aria-label');
        const closeLabel = toggle.dataset.closeLabel || openLabel;
        const bar = this.querySelector('.site-header__bar');

        // The panel opens under the header bar, so the logo, search, cart and
        // the toggle (now a cross) stay visible above it.
        const setPanelTop = () => {
          if (bar) this.style.setProperty('--mobile-panel-top', `${Math.max(bar.getBoundingClientRect().bottom, 0)}px`);
        };
        this.setPanelTop = setPanelTop;
        // The header moves up when the marquee scrolls away (CSS transition):
        // follow it to the end.
        this.addEventListener('transitionend', () => {
          if (this.classList.contains('mobile-menu-open')) setPanelTop();
        });

        let closeTimer = null;
        const setOpen = (isOpen) => {
          clearTimeout(closeTimer);
          this.classList.remove('mobile-menu-closing');
          if (isOpen) {
            setPanelTop();
          } else if (this.classList.contains('mobile-menu-open') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            // Let the panel slide back down before hiding it (see site-header.css).
            this.classList.add('mobile-menu-closing');
            closeTimer = setTimeout(() => {
              this.classList.remove('mobile-menu-closing', 'mobile-menu-open');
              if (this.closeMobileSubs) this.closeMobileSubs();
            }, 280);
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('aria-label', openLabel);
            document.body.classList.remove('overflow-hidden');
            return;
          }
          this.classList.toggle('mobile-menu-open', isOpen);
          if (!isOpen && this.closeMobileSubs) this.closeMobileSubs();
          toggle.setAttribute('aria-expanded', String(isOpen));
          toggle.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
          document.body.classList.toggle('overflow-hidden', isOpen);
        };

        const isOpen = () => this.classList.contains('mobile-menu-open') && !this.classList.contains('mobile-menu-closing');
        toggle.addEventListener('click', () => setOpen(!isOpen()));
        // The dark veil (site-header::before) also covers the header bar:
        // a tap on it lands on the header element itself and closes the menu.
        this.addEventListener('click', (event) => {
          if (event.target !== this) return;
          if (isOpen()) {
            setOpen(false);
            return;
          }
          // Phone filter panel open: its veil also covers the header.
          const filterClose = document.querySelector('.ys-toolbar details.menu-opening .mobile-facets__close');
          if (filterClose) filterClose.click();
        });
        window.addEventListener('resize', () => {
          if (this.classList.contains('mobile-menu-open')) setPanelTop();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.classList.contains('mobile-menu-open')) setOpen(false);
        });

        // Country / language: same window as the selector in the top bar.
        const localeButton = this.querySelector('[data-mobile-localization]');
        if (localeButton) {
          localeButton.addEventListener('click', () => {
            const modal = document.querySelector('dialog.marquee-modal');
            if (!modal) return;
            if (typeof modal.showModal === 'function') modal.showModal();
            else modal.setAttribute('open', '');
          });
        }

        // Montres / Services open their own screen, sliding in from the right
        // (the main list slides away to the left); "back" returns.
        const closeSubs = () => {
          panel.classList.remove('has-sub-open');
          this.querySelectorAll('[data-mobile-accordion].is-open').forEach((item) => item.classList.remove('is-open'));
        };
        this.closeMobileSubs = closeSubs;

        this.querySelectorAll('[data-mobile-accordion-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const item = trigger.closest('[data-mobile-accordion]');
            if (!item) return;
            closeSubs();
            panel.scrollTop = 0;
            item.classList.add('is-open');
            panel.classList.add('has-sub-open');
            const sub = item.querySelector('.site-header__mobile-sub');
            if (sub) sub.scrollTop = 0;
          });
        });

        this.querySelectorAll('[data-mobile-back]').forEach((back) => {
          back.addEventListener('click', closeSubs);
        });

        // Safety net: the menu must never end up scrolled sideways.
        panel.addEventListener('scroll', () => {
          if (panel.scrollLeft !== 0) panel.scrollLeft = 0;
        }, { passive: true });
      }

      setupCardScrollers() {
        this.querySelectorAll('[data-megamenu-cards]').forEach((track) => {
          const nextButton = track.parentElement.querySelector('[data-megamenu-cards-next]');
          const prevButton = track.parentElement.querySelector('[data-megamenu-cards-prev]');
          if (!nextButton) return;

          const getStep = () => {
            const card = track.querySelector('.site-header__megamenu-card');
            return card ? card.getBoundingClientRect().width + 28.8 : track.clientWidth * 0.8;
          };

          const updateVisibility = () => {
            const maxScroll = track.scrollWidth - track.clientWidth;
            nextButton.hidden = maxScroll <= 4 || track.scrollLeft >= maxScroll - 4;
            if (prevButton) prevButton.hidden = track.scrollLeft <= 4;
          };

          nextButton.addEventListener('click', () => {
            track.scrollBy({ left: getStep(), behavior: 'smooth' });
            nextButton.blur();
          });

          if (prevButton) {
            prevButton.addEventListener('click', () => {
              track.scrollBy({ left: -getStep(), behavior: 'smooth' });
              prevButton.blur();
            });
          }

          track.addEventListener('scroll', updateVisibility, { passive: true });
          window.addEventListener('resize', updateVisibility);
          updateVisibility();
        });
      }

      setupMegamenuClose() {
        this.querySelectorAll('.site-header__nav-item.has-megamenu').forEach((item) => {
          const megamenu = this.querySelector('.site-header__megamenu');
          if (!megamenu) return;

          const closeIfFocusStuck = (event) => {
            const goingTo = event.relatedTarget;
            if (goingTo && (item.contains(goingTo) || megamenu.contains(goingTo))) return;
            if (item.contains(document.activeElement) || megamenu.contains(document.activeElement)) {
              document.activeElement.blur();
            }
          };

          item.addEventListener('mouseleave', closeIfFocusStuck);
          megamenu.addEventListener('mouseleave', closeIfFocusStuck);
        });
      }
    }
  );
}
